import { useEffect, useState } from 'react'
import {
  chaveDaVariavel,
  preencherModelo,
  ROTULO_CATEGORIA_MODELO,
  tempoAteFechar,
  variaveisSemValor,
  type ModeloOficialDto,
} from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import { useEnviarModelo, useModelosDaConversa } from '../../api/hooks-api-oficial'
import { IconeModeloAprovado } from '../../componentes/icones'
import { CLASSE_DO_ROTULO, Vazio } from '../../componentes/pagina'
import { TextoWhatsApp } from '../../componentes/TextoWhatsApp'
import { Aviso, Botao, Campo, Carregando, Modal, juntar } from '../../componentes/ui'

/**
 * Modelos aprovados da API oficial (ADR 0033).
 *
 * Com a janela de 24 horas fechada, é o único envio que a Meta aceita. A
 * escolha mora num componente só, usado pela conversa e pelo Agendar: o
 * mesmo modelo se escolhe e se preenche do mesmo jeito nos dois lugares.
 */

export interface EscolhaDoModelo {
  modeloId: string | null
  /** Valor de cada variável, pela chave de `chaveDaVariavel`. */
  valores: Record<string, string>
}

export const ESCOLHA_VAZIA: EscolhaDoModelo = { modeloId: null, valores: {} }

/** O que falta para o modelo escolhido poder sair. Vazio quando está pronto. */
export function pendenciaDoModelo(
  modelos: ModeloOficialDto[] | undefined,
  escolha: EscolhaDoModelo,
): string | null {
  const escolhido = modelos?.find((m) => m.id === escolha.modeloId)
  if (!escolhido) return 'Escolha um modelo.'
  const faltando = variaveisSemValor(escolhido.analise, escolha.valores)
  if (faltando.length > 0) return `Preencha ${faltando.map((v) => `{{${v.nome}}}`).join(', ')}.`
  return null
}

export function EscolhaDeModelo({
  modelos,
  escolha,
  aoMudar,
}: {
  modelos: ModeloOficialDto[]
  escolha: EscolhaDoModelo
  aoMudar: (escolha: EscolhaDoModelo) => void
}) {
  const escolhido = modelos.find((m) => m.id === escolha.modeloId) ?? null

  if (modelos.length === 0) {
    return (
      <Vazio>
        Nenhum modelo aprovado neste número. O proprietário busca os modelos na Meta em
        Configuração, Números, API oficial.
      </Vazio>
    )
  }

  return (
    <div className="space-y-4">
      <ul className="max-h-56 space-y-1.5 overflow-y-auto p-0.5 pr-1.5">
        {modelos.map((m) => {
          const marcado = m.id === escolha.modeloId
          return (
            <li key={m.id}>
              <button
                type="button"
                disabled={!m.analise.enviavel}
                aria-pressed={marcado}
                onClick={() => aoMudar({ modeloId: m.id, valores: {} })}
                className={juntar(
                  'w-full rounded-md px-3 py-2 text-left transition',
                  marcado
                    ? 'bg-marca/5 ring-2 ring-marca'
                    : 'ring-1 ring-tinta-200 hover:bg-tinta-50',
                  !m.analise.enviavel && 'cursor-not-allowed opacity-60 hover:bg-transparent',
                )}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-tinta-900">{m.nome}</span>
                  <span className="shrink-0 text-[11px] text-tinta-400">
                    {ROTULO_CATEGORIA_MODELO[m.categoria] ?? m.categoria}
                  </span>
                </span>
                <span className="mt-0.5 line-clamp-2 block text-xs text-tinta-500">
                  {m.analise.corpo}
                </span>
                {!m.analise.enviavel ? (
                  <span className="mt-0.5 block text-xs text-amber-700">{m.analise.motivo}</span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>

      {escolhido ? (
        <>
          {escolhido.analise.variaveis.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {escolhido.analise.variaveis.map((v) => {
                const chave = chaveDaVariavel(v)
                return (
                  <Campo
                    key={chave}
                    rotulo={`${v.onde === 'cabecalho' ? 'Cabeçalho' : 'Texto'}: {{${v.nome}}}`}
                    value={escolha.valores[chave] ?? ''}
                    maxLength={1024}
                    placeholder={v.exemplo ? `ex.: ${v.exemplo}` : undefined}
                    onChange={(e) =>
                      aoMudar({
                        ...escolha,
                        valores: { ...escolha.valores, [chave]: e.target.value },
                      })
                    }
                  />
                )
              })}
            </div>
          ) : null}

          <div>
            <p className={CLASSE_DO_ROTULO}>Como o cliente vai ler</p>
            <div className="mt-1.5 whitespace-pre-wrap rounded-md bg-tinta-50 px-3 py-2 text-sm text-tinta-800">
              <TextoWhatsApp texto={preencherModelo(escolhido.analise, escolha.valores)} />
            </div>
            {escolhido.analise.botoes.length > 0 ? (
              <p className="mt-1.5 text-xs text-tinta-400">
                Botões: {escolhido.analise.botoes.join(', ')}
              </p>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  )
}

/** A janela de mandar um modelo, aberta pela conversa. */
export function JanelaDeModelos({
  chamadoId,
  janelaAberta,
  aoFechar,
}: {
  chamadoId: string
  janelaAberta: boolean
  aoFechar: () => void
}) {
  const { data: modelos, isLoading } = useModelosDaConversa(chamadoId, true)
  const enviar = useEnviarModelo()
  const [escolha, setEscolha] = useState<EscolhaDoModelo>(ESCOLHA_VAZIA)

  const pendencia = pendenciaDoModelo(modelos, escolha)
  const erro = enviar.error instanceof ErroRequisicao ? enviar.error.corpo.mensagem : null

  return (
    <Modal
      aberto
      titulo="Mandar modelo aprovado"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            disabled={pendencia !== null}
            carregando={enviar.isPending}
            title={pendencia ?? undefined}
            onClick={() => {
              if (!escolha.modeloId) return
              enviar.mutate(
                { chamadoId, dados: { modeloId: escolha.modeloId, valores: escolha.valores } },
                { onSuccess: aoFechar },
              )
            }}
          >
            Enviar modelo
          </Botao>
        </>
      }
    >
      <p className="mb-3 text-sm text-tinta-500">
        {janelaAberta
          ? 'A janela de 24 horas está aberta, então texto livre também sai. O modelo serve para retomar um cliente parado.'
          : 'A janela de 24 horas fechou. Até o cliente responder, só um modelo aprovado pela Meta pode ser enviado.'}
      </p>
      {erro ? (
        <div className="mb-3">
          <Aviso tipo="erro">{erro}</Aviso>
        </div>
      ) : null}
      {isLoading ? (
        <Carregando />
      ) : (
        <EscolhaDeModelo modelos={modelos ?? []} escolha={escolha} aoMudar={setEscolha} />
      )}
    </Modal>
  )
}

/**
 * O relógio da janela: a janela fecha sozinha com o tempo, sem mensagem nova
 * nenhuma, e a conversa aberta na tela precisa perceber. Um minuto basta.
 */
export function useAgoraDoMinuto(ligado: boolean): number {
  const [agora, setAgora] = useState(() => Date.now())
  useEffect(() => {
    if (!ligado) return
    const relogio = window.setInterval(() => setAgora(Date.now()), 60_000)
    return () => window.clearInterval(relogio)
  }, [ligado])
  return agora
}

/**
 * A faixa sobre o campo de escrever, só em número da API oficial.
 *
 * Aberta, é uma linha cinza com o tempo que falta: quem atende sabe até
 * quando pode responder de graça e livre. Fechada, diz o que fazer e oferece
 * o caminho, em vez de deixar a pessoa escrever e ver a Meta recusar.
 */
export function FaixaDaJanela({
  aberta,
  fechaEm,
  agora,
  podeEnviar,
  aoEscolherModelo,
}: {
  aberta: boolean
  fechaEm: Date | null
  agora: number
  podeEnviar: boolean
  aoEscolherModelo: () => void
}) {
  if (aberta && fechaEm) {
    return (
      <p className="mb-1.5 px-1 text-xs text-tinta-400">
        API oficial: janela aberta, fecha em {tempoAteFechar(fechaEm, new Date(agora))}.
      </p>
    )
  }
  return (
    <div className="mb-2 flex flex-wrap items-center gap-3 rounded-md bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
      <p className="min-w-0 flex-1 text-sm text-amber-900">
        {fechaEm
          ? 'A janela de 24 horas fechou. Até o cliente responder, só um modelo aprovado sai por este número.'
          : 'O cliente ainda não escreveu por este número. Pela API oficial, a conversa começa com um modelo aprovado.'}
      </p>
      {podeEnviar ? (
        <Botao onClick={aoEscolherModelo}>
          <IconeModeloAprovado className="h-4 w-4" />
          Escolher modelo
        </Botao>
      ) : null}
    </div>
  )
}
